/**
 * Redux action types + creators for the Five9 slice.
 *
 * The slice owns:
 *   - `calls`: a map of `{ [callSid]: Five9Call }` so multiple concurrent
 *     Five9 interactions can be tracked.
 *   - `activeCallSid`: the callSid the UI/dispatch layer should treat as
 *     "current" (typically the most recently started/updated).
 *   - `log`: a ring-buffer of events emitted by Five9 callbacks + the
 *     adapter's SDK lifecycle. Displayed by `Five9LogPanel`.
 *
 * All Five9 SDK/window access stays confined to `Five9CrmAdapter` and
 * `useFive9CrmCallbacks`; dispatch code interacts with this slice only via
 * `useCurrentFive9Call`.
 *
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 * @typedef {import('src/type-defs/five9Types').Five9LogEntry} Five9LogEntry
 * @typedef {import('src/type-defs/five9Types').Five9PersistedSnapshot} Five9PersistedSnapshot
 */

/** @type {'FIVE9_UPSERT_CALL'} */
export const FIVE9_UPSERT_CALL = 'FIVE9_UPSERT_CALL';
/** @type {'FIVE9_SET_ACTIVE_CALL'} */
export const FIVE9_SET_ACTIVE_CALL = 'FIVE9_SET_ACTIVE_CALL';
/** @type {'FIVE9_ASSOCIATE_DISPATCH'} */
export const FIVE9_ASSOCIATE_DISPATCH = 'FIVE9_ASSOCIATE_DISPATCH';
/** @type {'FIVE9_CLEAR_CALL'} */
export const FIVE9_CLEAR_CALL = 'FIVE9_CLEAR_CALL';
/** @type {'FIVE9_LOG_APPEND'} */
export const FIVE9_LOG_APPEND = 'FIVE9_LOG_APPEND';
/** @type {'FIVE9_LOG_CLEAR'} */
export const FIVE9_LOG_CLEAR = 'FIVE9_LOG_CLEAR';
/** @type {'FIVE9_HYDRATE'} */
export const FIVE9_HYDRATE = 'FIVE9_HYDRATE';
/** @type {'FIVE9_RESET'} */
export const FIVE9_RESET = 'FIVE9_RESET';
/** @type {'FIVE9_SET_CLICK_TO_DIAL'} */
export const FIVE9_SET_CLICK_TO_DIAL = 'FIVE9_SET_CLICK_TO_DIAL';

/**
 * Create-or-merge a call into `state.five9.calls`. The reducer preserves
 * existing fields against `null`/`undefined` values (via `compact`) so a
 * partial event (e.g. `interactionOpened` with only `interactionId`)
 * never wipes out fields a prior event supplied.
 *
 * @param {Partial<Five9Call> & { callSid: string }} call
 * @returns {{ type: typeof FIVE9_UPSERT_CALL, payload: Partial<Five9Call> & { callSid: string } }}
 * @see docs/five9-payload-schema.md § 1 for identifier semantics.
 */
export const upsertCall = (call) => ({
  type: FIVE9_UPSERT_CALL,
  payload: call
});

/**
 * Set (or clear with `null`) the `activeCallSid`. The reducer rejects
 * non-callSid-looking values (e.g. `agent:foo@bar.com`).
 *
 * @param {string|null} callSid
 * @returns {{ type: typeof FIVE9_SET_ACTIVE_CALL, payload: { callSid: string|null } }}
 */
export const setActiveCall = (callSid) => ({
  type: FIVE9_SET_ACTIVE_CALL,
  payload: { callSid }
});

/**
 * Bind a dispatch id to a call. Passing `null` for `callSid` targets the
 * current `activeCallSid`. Passing `null` for `dispatchId` clears an
 * existing association.
 *
 * @param {string|null} callSid
 * @param {string|number|null} dispatchId
 * @returns {{ type: typeof FIVE9_ASSOCIATE_DISPATCH, payload: { callSid: string|null, dispatchId: string|number|null } }}
 */
export const associateDispatch = (callSid, dispatchId) => ({
  type: FIVE9_ASSOCIATE_DISPATCH,
  payload: { callSid, dispatchId }
});

/**
 * Remove a call from the slice. Also clears `activeCallSid` if it
 * pointed at the removed call.
 *
 * @param {string} callSid
 * @returns {{ type: typeof FIVE9_CLEAR_CALL, payload: { callSid: string } }}
 */
export const clearCall = (callSid) => ({
  type: FIVE9_CLEAR_CALL,
  payload: { callSid }
});

/**
 * Append one or many log entries. Accepts an array so the logger can batch
 * bursts within a single animation frame into a single dispatch.
 *
 * @param {Five9LogEntry | Five9LogEntry[]} entries
 * @returns {{ type: typeof FIVE9_LOG_APPEND, payload: Five9LogEntry[] }}
 */
export const logAppend = (entries) => ({
  type: FIVE9_LOG_APPEND,
  payload: Array.isArray(entries) ? entries : [entries]
});

/**
 * Empty the `log` ring buffer.
 *
 * @returns {{ type: typeof FIVE9_LOG_CLEAR }}
 */
export const logClear = () => ({ type: FIVE9_LOG_CLEAR });

/**
 * Seed the slice from a persisted snapshot (sessionStorage) or a cross-tab
 * handover message. Only `calls` + `activeCallSid` are hydrated; the log is
 * intentionally session-in-memory only.
 *
 * @param {Five9PersistedSnapshot} snapshot
 * @returns {{ type: typeof FIVE9_HYDRATE, payload: Five9PersistedSnapshot }}
 */
export const hydrateFive9 = (snapshot) => ({
  type: FIVE9_HYDRATE,
  payload: snapshot
});

/**
 * Reset the slice to its initial (empty) state. Used on auth transitions
 * (logout, user switch) so a previous user's calls/associations/log cannot
 * leak into a subsequent authenticated session in the same tab. Pair with
 * `clearPersistedFive9()` from `src/store` and the reset of the singleton
 * iframe (`persistentFive9Iframe.reset()`) to fully purge Five9 state.
 *
 * @returns {{ type: typeof FIVE9_RESET }}
 */
export const resetFive9 = () => ({ type: FIVE9_RESET });

/**
 * Record whether the ADT currently accepts click-to-dial. Driven by the
 * `crm.enableClickToDial` / `crm.disableClickToDial` callbacks, which the
 * live ADT invokes on agent login/logout AND around every call (disabled
 * while a call is active, re-enabled after wrap-up) — per-call availability,
 * not a connection signal.
 *
 * @param {boolean} enabled
 * @returns {{ type: typeof FIVE9_SET_CLICK_TO_DIAL, payload: { enabled: boolean } }}
 */
export const setClickToDialEnabled = (enabled) => ({
  type: FIVE9_SET_CLICK_TO_DIAL,
  payload: { enabled: !!enabled }
});
